import { useEffect, useRef, useState } from 'react';
import {
  DEFAULT_THEME_ID,
  getThemePresetById,
  THEME_PRESETS,
  type ThemePreset,
} from '@/modules/shared/styles/themePresets';

const STORAGE_KEY = 'y-theme-preset';

function applyTheme(themeId: string): void {
  // golden-hour is the :root default in tokens.css, so it needs no data-theme attr.
  // All other themes (including DEFAULT_THEME_ID) must set the attr to activate CSS overrides.
  if (themeId === 'golden-hour') {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = themeId;
  }
}

export default function ThemeColorPicker() {
  const [currentThemeId, setCurrentThemeId] = useState<string>(DEFAULT_THEME_ID);
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const savedThemeId = localStorage.getItem(STORAGE_KEY) ?? DEFAULT_THEME_ID;
    const theme = getThemePresetById(savedThemeId);
    const nextThemeId = theme?.id ?? DEFAULT_THEME_ID;

    setCurrentThemeId(nextThemeId);
    applyTheme(nextThemeId);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!expanded) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setExpanded(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [expanded]);

  const handleThemeChange = (theme: ThemePreset): void => {
    setCurrentThemeId(theme.id);
    applyTheme(theme.id);
    localStorage.setItem(STORAGE_KEY, theme.id);
    setExpanded(false);
  };

  const currentTheme = getThemePresetById(currentThemeId) ?? THEME_PRESETS[0];

  return (
    <div ref={containerRef} className="relative flex items-center">
      {/* Collapsed: show only current theme dot */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className={[
          'w-5 h-5 rounded-full cursor-pointer',
          'transition-all duration-200 ease-out',
          'hover:scale-110',
          'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--y-accent)]',
          'ring-2 ring-offset-2 ring-[var(--y-accent)]',
        ].join(' ')}
        style={{ backgroundColor: currentTheme.displayColor, border: 'none' }}
        title={expanded ? 'Close theme picker' : 'Change theme color'}
        aria-label={expanded ? 'Close theme picker' : 'Change theme color'}
        aria-expanded={expanded}
      />

      {/* Expanded: dropdown with all options */}
      {expanded && (
        <div className="absolute top-full right-0 mt-2 p-2 rounded-xl bg-[var(--y-surface-secondary)] border border-[var(--y-border)] shadow-lg flex items-center gap-2 z-50">
          {THEME_PRESETS.map((theme) => {
            const isActive = theme.id === currentThemeId;

            return (
              <button
                key={theme.id}
                type="button"
                onClick={() => handleThemeChange(theme)}
                data-theme-id={theme.id}
                className={[
                  'w-6 h-6 rounded-full cursor-pointer',
                  'transition-all duration-200 ease-out',
                  'hover:scale-110',
                  'focus:outline-none focus:ring-2 focus:ring-offset-2',
                  isActive ? 'ring-2 ring-offset-2 ring-[var(--y-accent)]' : '',
                ].join(' ')}
                style={{ backgroundColor: theme.displayColor, border: 'none' }}
                title={theme.name}
                aria-label={`Select theme: ${theme.name}`}
                aria-pressed={isActive}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}
